import Editor from '@monaco-editor/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Form, Input, Select, Space } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

/**
 * APIs
 */
import { cConfig, rConfig, uConfig } from '@/apis/config';

/**
 * Contexts
 */
import { useLayoutStore } from '@/store/layout';

/**
 * Styles
 */
import styles from './index.module.css';

/**
 * Types
 */
import type { FormProps } from 'antd';

const ComponentCreateEdit = () => {
    /**
     * Hooks
     */
    const [form] = Form.useForm();

    const { setPageContainer } = useLayoutStore();

    const navigate = useNavigate();

    const params = useParams();

    const { t } = useTranslation();

    /**
     * Requests
     */
    const { isPending: pendingCConfig, mutate: mutateCConfig } = useMutation({
        mutationKey: ['cConfig'],
        mutationFn: cConfig,
        onSuccess: () => navigate('/system-management/configs'),
    });

    const { isPending: pendingUConfig, mutate: mutateUConfig } = useMutation({
        mutationKey: ['uConfig'],
        mutationFn: uConfig,
        onSuccess: () => navigate('/system-management/configs'),
    });

    useQuery({
        enabled: !!params.id,
        queryKey: ['rConfig', params.id],
        queryFn: () =>
            rConfig(params.id!).then((data) => {
                form.setFieldsValue({
                    ...data.data,
                    value: JSON.stringify(data.data.value, null, 4),
                });

                return data;
            }),
    });

    /**
     * ChildrenProps
     */
    const formProps: FormProps = {
        form,
        initialValues: {
            type: 'private',
        },
        layout: 'vertical',
        onFinish: (values) => {
            values.value = JSON.parse(values.value);

            if (params.id) {
                return mutateUConfig(values);
            }

            mutateCConfig(values);
        },
    };

    /**
     * Effects
     */
    useEffect(() => {
        setPageContainer({
            extra: (
                <Link to="/system-management/configs">
                    <Button type="primary">{t('back')}</Button>
                </Link>
            ),
        });

        return () => {
            setPageContainer({});
        };
    }, []);

    return (
        <Form {...formProps}>
            <Form.Item hidden name="id">
                <Input />
            </Form.Item>

            <Form.Item hidden name="version">
                <Input />
            </Form.Item>

            <Space>
                <Form.Item label={t('page.configs.key')} name="key" rules={[{ required: true }]}>
                    <Input style={{ width: 400 }} />
                </Form.Item>

                <Form.Item label={t('type')} name="type" rules={[{ required: true }]}>
                    <Select
                        options={[
                            { label: t('page.configs.private'), value: 'private' },
                            { label: t('page.configs.public'), value: 'public' },
                        ]}
                        placeholder={t('type')}
                        style={{ width: 200 }}
                    />
                </Form.Item>
            </Space>

            <Form.Item
                label={t('page.configs.value')}
                name="value"
                rules={[{ required: true }]}
                style={{ height: 'calc(100dvh - 342px)' }}
            >
                <Editor className={styles.editor} defaultLanguage="json" loading={t('loading')} />
            </Form.Item>

            <Button htmlType="submit" loading={pendingCConfig || pendingUConfig} type="primary">
                {t('save')}
            </Button>
        </Form>
    );
};

export default ComponentCreateEdit;
